import { createAsyncThunk } from '@reduxjs/toolkit';
import { ConversationStarter, QueryParams } from '../../types';
import { Paginated, PaginationService } from '../../services/api-handlers/pagination';
import { ConversationStarterApi } from '../../services/end-points';
import { resolveApiCall } from '../../services/api-handlers/api-resolver';
import SortingService from '../../services/sorting';
import { ConversationStarterFormData } from '../../screens/ConversationStarter/components/ConversationStarterForm';
import { makeFormErrorsFromResponse } from '../../components/react-hook-form/utils/make-form-errors';
import { ThunkConfig } from '../configure-store';

export const listConversationStarters = createAsyncThunk<Paginated<ConversationStarter>, QueryParams, ThunkConfig>(
    'conversation-starter/list',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { conversationStarter } = getState();
        const { filters, sorting, ...rest } = payload;

        return PaginationService.resolveApiCall(
            thunkApi,
            conversationStarter,
            async () =>
                ConversationStarterApi.list({
                    ...filters,
                    ...SortingService.makeOrder(sorting),
                    ...rest,
                }),
            payload.page,
            conversationStarter.data,
        );
    },
);

export const getAllConversationStarters = createAsyncThunk<Paginated<ConversationStarter>, undefined, ThunkConfig>(
    'conversation-starter/all-list',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { conversationStarter } = getState();

        return PaginationService.resolveApiCall(
            thunkApi,
            conversationStarter,
            async () =>
                ConversationStarterApi.list({
                    limit: -1,
                }),
            1,
            {
                list: [],
                page: 1,
            },
        );
    },
);

export const getConversationStarter = createAsyncThunk<
    ConversationStarter,
    { id: string; onError: (data: any) => any },
    ThunkConfig
>('conversation-starter/current', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarter } = getState();
    const { id, onError } = payload;

    return resolveApiCall(
        thunkApi,
        conversationStarter,
        async () => (await ConversationStarterApi.get(id)).data,
        onError,
    );
});

export const deleteConversationStarter = createAsyncThunk<any, { id: string; callback: () => void }, ThunkConfig>(
    'conversation-starter/delete',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { id } = payload;
        const { conversationStarter } = getState();
        await resolveApiCall(
            thunkApi,
            conversationStarter,
            async () => (
                await ConversationStarterApi.remove(id),
                async (err: { response: any }) => {
                    const { response } = err;
                    return makeFormErrorsFromResponse(response.data);
                }
            ),
        );
        payload.callback();
    },
);

export const createConversationStarter = createAsyncThunk<
    ConversationStarter,
    ConversationStarterFormData,
    ThunkConfig
>('conversation-starter/create', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarter } = getState();
    return resolveApiCall(
        thunkApi,
        conversationStarter,
        async () => await ConversationStarterApi.create(payload),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});

export const updateConversationStarter = createAsyncThunk<
    ConversationStarter,
    { id: ConversationStarter['id']; data: Partial<ConversationStarterFormData> },
    ThunkConfig
>('conversation-starter/update', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { conversationStarter } = getState();
    const { id, data } = payload;
    return resolveApiCall(
        thunkApi,
        conversationStarter,
        async () => await ConversationStarterApi.update(id, data),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});
